import PortalDocument, {PortalDocumentInterface} from "../../PortalDocument";
import React, {createRef, ReactNode, RefObject} from "react";
import {Button} from "primereact/button";
import {Tree, TreeCheckboxSelectionKeys, TreeMultipleSelectionKeys} from "primereact/tree";
import {InputText} from "primereact/inputtext";
import {Checkbox} from "primereact/checkbox";
import {pdfjs} from "react-pdf";
import {PdfPageDefinition} from "../../../viewer/pdf/PdfPage";
import Observer from "../../../../shared/class/Observer";
import LocationEvent from "../../../viewer/LocationEvent";
import {MetadataRectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import DocumentSettings from "../../DocumentSettings";
import SidebarLeftMode from "../../../viewer/settings/SidebarLeftMode";
import TreeNode from "primereact/treenode";
import {Paginator} from "primereact/paginator";
import Translate from "../../../../shared/translation/Translate";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import UnitHelper from "../../../../shared/helper/UnitHelper";
import LengthUnit from "../../../../shared/units/LengthUnit";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";
import EscapeHelper from "../../../../shared/helper/EscapeHelper";

export interface TextSearchGlobals {
	textSearchResults?: Immutable<Array<TreeNode>>;
	textSearchSelection?: string | TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys | null;
}

export function initTextSearchGlobals(): TextSearchGlobals {
	return {
		textSearchResults: Immutable.from([]),
		textSearchSelection: null,
	};
}

interface TextSearchProperties extends TextSearchGlobals, I18nGlobals, DebugGlobals {
	visible: boolean;
	context: PortalContext;
	portalDocument?: Immutable<PortalDocumentInterface>;
	locationObserver?: Observer<LocationEvent>;
}

interface TextSearchStatus {
	query: string;
	case: boolean;
	words: boolean;
	searching: boolean;
	currentSearchPage: number;
}

export class TextSearchDefinition extends PortalPureComponent<TextSearchProperties, TextSearchStatus> {
	private searchInput: RefObject<any>;
	private readonly ROW_SIZE: number = 50;

	/**
	 * Text search component constructor
	 */
	constructor(props: Readonly<TextSearchProperties>) {
		super(props);

		this.state = {
			query: "",
			case: false,
			words: false,
			searching: false,
			currentSearchPage: 0
		};

		this.searchInput = createRef<any>();

		this.handleSelect = this.handleSelect.bind(this);
		this.buildSearchList = this.buildSearchList.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
		this.handleDocumentChange = this.handleDocumentChange.bind(this);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any
	): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		if (this.props.portalDocument!.get().id !== prevProps.portalDocument.get().id ||
			this.props.portalDocument!.get().updated !== prevProps.portalDocument.get().updated) {
			this.handleDocumentChange();
		}

		if (documentSettings.sidebarLeftMode === SidebarLeftMode.SEARCH &&
			FunctionHelper.exists(this.searchInput.current)) {
			this.searchInput.current.focus();
		}
	}

	private handleDocumentChange() {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		this.setState({
			query: documentSettings.searchQuery
		}, () => this.props.context.getPage().setState({
			textSearchResults: Immutable.from([]),
			textSearchSelection: null
		}, async () => {
			await this.buildSearchList();
		}));
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		window.addEventListener('keydown', this.handleKeydown);

		if (FunctionHelper.exists(this.searchInput.current)) {
			this.searchInput.current.focus();
		}

		this.handleDocumentChange();
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		window.removeEventListener('keydown', this.handleKeydown);
	}

	/**
	 * called when the component is mounted
	 */
	public async buildSearchList(): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		this.props.context.getPage().setState({
			textSearchResults: Immutable.from([]),
			textSearchSelection: null
		}, () => this.setState({
			searching: true,
			currentSearchPage: 0
		}));

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		let searchValue: string = this.state.query.trim();
		if (searchValue === '') {
			this.props.context.getPage().setState({
				textSearchResults: Immutable.from([]),
				textSearchSelection: null
			}, () => this.setState({
				searching: false
			}, () => {
				if (FunctionHelper.exists(this.searchInput.current)) {
					this.searchInput.current.focus();
				}
			}));
			return;
		}

		try {
			let pdfDocument: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let expressionValue: string = EscapeHelper.escapeRegex(searchValue);
			let flags: string = '';

			if (this.state.words) {
				expressionValue = "\\b" + expressionValue + "\\b";
			}

			if (!this.state.case) {
				flags += 'i';
			}

			let expression: RegExp = new RegExp(expressionValue, flags);
			let results: Array<TreeNode> = [];
			let resultCount: number = 1;

			for (let pageNumber: number = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
				let pdfPage: pdfjs.PDFPageProxy = await pdfDocument.getPage(pageNumber);
				let textContent: any = await pdfPage.getTextContent();

				for (let textItem of textContent.items) {
					if (expression.test(textItem.str)) {
						results.push({
							key: resultCount,
							label: textItem.str.replace(
								expression, "<span class=\"search-value\">$&</span>"
							),
							data: {...textItem, page: pageNumber},
							children: []
						});

						resultCount++;
					}
				}
			}

			this.props.context.getPage().setState({
				textSearchResults: Immutable.from(results),
				textSearchSelection: null
			}, () => this.setState({
				searching: false
			}, () => {
				if (FunctionHelper.exists(this.searchInput.current)) {
					this.searchInput.current.focus();
				}
			}));
		} catch (e: any) {
			// can be ignored here
		}
	}

	/**
	 * This is called if a tree node is selected
	 */
	private async handleSelect(node: TreeNode): Promise<void> {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return;
		}

		try {
			let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
				this.props.portalDocument!.get().id!
			);
			let pdfDocument: pdfjs.PDFDocumentProxy = await portalDocument.loadViewerData();
			let pdfPage: pdfjs.PDFPageProxy = await pdfDocument.getPage(node.data.page);
			let rotation: number = PdfPageDefinition.calculateRotationSteps(pdfPage.rotate);
			let target: MetadataRectangle = MetadataRectangle.fromJson({
				height: UnitHelper.unitToPx(node.data.height, LengthUnit.POINTS, this.props.portalResolution),
				width: UnitHelper.unitToPx(node.data.width, LengthUnit.POINTS, this.props.portalResolution),
				x: UnitHelper.unitToPx(node.data.transform[4], LengthUnit.POINTS, this.props.portalResolution),
				y: UnitHelper.unitToPx(node.data.transform[5], LengthUnit.POINTS, this.props.portalResolution)
			} as MetadataRectangle);

			switch (rotation) {
				case 0:
					target.y! -= target.height!;
					break;
				case 1:
					target.y! += target.height!;
					break;
				case 2:
					target.y! += target.height!;
					target.x! += target.width!;
					break;
				case 3:
					target.y! -= target.height!;
					target.x! += target.width!;
					break;
			}

			if (FunctionHelper.exists(this.props.locationObserver)) {
				this.props.locationObserver!.notify({
					target: target,
					pageNumber: node.data.page
				});
			}

			this.props.context.getPage().setState({
				textSearchSelection: node.key as string
			});
		} catch (e: any) {
			// can be ignored here
		}
	}

	/**
	 * Handles keyboard key down events
	 */
	private async handleKeydown(e: KeyboardEvent) {
		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);

		if (e.key.toLowerCase() === "enter" && !portalDocument.isLocked()) {
			await this.buildSearchList();
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (!FunctionHelper.exists(this.props.portalDocument?.value)) {
			return null;
		}

		let portalDocument: PortalDocument = this.props.context.documentManager.findDocument(
			this.props.portalDocument!.get().id!
		);
		let documentSettings: DocumentSettings = portalDocument.getSettings();

		let searchResults: Array<TreeNode> = FunctionHelper.applyValue(
			this.props.textSearchResults, Immutable.from([])
		).get();
		let currentPageResults: Array<TreeNode> =
			searchResults.slice(this.state.currentSearchPage, this.state.currentSearchPage + this.ROW_SIZE);

		let searchIcon = ["icon"];
		if (this.state.searching) {
			searchIcon = searchIcon.concat(["icon-spinner", "spin"]);
		} else {
			searchIcon.push("icon-check");
		}

		return (
			<div className="body" style={this.props.visible ? {} : {display: "none"}}
				 data-help="global.sidebar.search" data-position="right">
				<div className="toolbar">
					<label>
						<Checkbox checked={this.state.case}
								  disabled={this.state.searching || portalDocument.isLocked()}
								  onChange={() => {
									  this.setState({case: !this.state.case}, async () => {
										  await this.buildSearchList();
									  });
								  }}/>
						<span>
							 <Translate {...this.props} data="search.case" namespace="sidebar"/>
						</span>
					</label>
					<label>
						<Checkbox checked={this.state.words}
								  disabled={this.state.searching || portalDocument.isLocked()}
								  onChange={() => {
									  this.setState({words: !this.state.words}, async () => {
										  await this.buildSearchList();
									  });
								  }}/>
						<span>
							 <Translate {...this.props} data="search.words" namespace="sidebar"/>
						</span>
					</label>
				</div>
				<div className="toolbar stretched">
					<div className="p-col">
						<div className="p-inputgroup">
							<Button icon="icon icon-close"
									disabled={this.state.searching || portalDocument.isLocked()}
									tooltip={Translate.value("search.search.delete", {ns: "sidebar"})}
									onClick={() => {
										documentSettings.searchQuery = "";

										this.setState({
												query: documentSettings.searchQuery
											}, () => {
												this.props.context.getPage().setState({
													textSearchResults: Immutable.from([]),
													textSearchSelection: null
												}, () => {
													if (FunctionHelper.exists(this.searchInput.current)) {
														this.searchInput.current.focus();
													}
												});
											}
										);
									}}/>
							<InputText type="search" value={this.state.query} ref={this.searchInput} autoFocus={true}
									   disabled={this.state.searching || portalDocument.isLocked()}
									   placeholder={Translate.value(
										   "search.search.placeholder", {ns: "sidebar"}
									   )}
									   onChange={(event) => {
										   documentSettings.searchQuery = (event.target as HTMLInputElement).value;

										   this.setState({
											   query: documentSettings.searchQuery
										   });
									   }}/>
							<Button icon={searchIcon.join(" ")} onClick={this.buildSearchList}
									disabled={this.state.searching || portalDocument.isLocked()}
									tooltip={Translate.value("search.search.confirm", {ns: "sidebar"})}/>
						</div>
					</div>
				</div>
				<div className="text-search">
					{currentPageResults.length > 0 ?
						<Tree selectionKeys={this.props.textSearchSelection} value={currentPageResults}
							  propagateSelectionDown={false} selectionMode="single"
							  nodeTemplate={(node: any) => {
								  return (
									  <Button tooltip={Translate.value(
										  "search.page", {ns: "sidebar", page: node.data.page}
									  )} onClick={async () => {
										  await this.handleSelect(node);
									  }}>
										  <span className="text-search-tooltip"
												dangerouslySetInnerHTML={{__html: node.label}}/>
									  </Button>
								  );
							  }}/> :
						<p className="no-results">
							<Translate {...this.props} data="search.empty" namespace="sidebar"/>
						</p>
					}
				</div>
				<div className="toolbar inverted centered">
					{searchResults.length > this.ROW_SIZE &&
						<Paginator totalRecords={searchResults.length} rows={this.ROW_SIZE}
								   first={this.state.currentSearchPage}
								   onPageChange={(event) => this.setState({
									   currentSearchPage: event.first
								   })}/>
					}
				</div>
			</div>
		);
	}
}

const TextSearch = React.memo(TextSearchDefinition,
	(
		prevProps: Readonly<TextSearchProperties>,
		nextProps: Readonly<TextSearchProperties>
	) => {
		if (FunctionHelper.exists(nextProps.portalDocument) &&
			!nextProps.portalDocument!.equals(prevProps.portalDocument)) {
			return false;
		}

		if (FunctionHelper.exists(nextProps.textSearchResults) &&
			!nextProps.textSearchResults!.equals(prevProps.textSearchResults)) {
			return false;
		}

		if (nextProps.textSearchSelection !== prevProps.textSearchSelection) {
			return false;
		}

		if (nextProps.language !== prevProps.language) {
			return false;
		}

		return true;
	}
);

export default TextSearch;